import CoreBase from '../../../services/engines/CoreBase';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import UploadAttachmentHandlerFactory from './UploadAttachmentHandlerFactory';

const _name = 'UploadAttachmentHandler';
const _event = 'uploadStateChanged';

/**
 * Singleton handler for the upload attachment dialog.
 * Owns all upload state — files, selectedIndex, uploadState, uploadProgress.
 * Routes events to sub-handlers via factory map.
 * Components call dispatch(eventName, data) — never touch state directly.
 */
class UploadAttachmentHandler extends CoreBase {
    _name = _name;
    _jid = '';

    // Array of UploadFileItem
    files = [];
    selectedIndex = 0;

    // 'idle' | 'uploading' | 'done'
    uploadState = 'idle';

    // { fileName, current, total } — null when idle
    uploadProgress = null;

    constructor() {
        super();
        // Pass this as state — handlers mutate properties directly
        this._handlers = new UploadAttachmentHandlerFactory().create(this);
    }

    /**
     * set target journal rk also known as jid
     *
     * @param {string} jid The journal rk as owner of attachments
     */
    setJid = (jid) => {
        this._jid = jid;
    };

    /**
     * Routes an event to the correct handler.
     * Never modify this method — add handlers to the factory instead.
     * @param {string} event - event name matching a key in the handlers map
     * @param {object} data - payload passed to handler.handle(data)
     */
    dispatch = (event, data) => {
        try {
            const handler = this._handlers[event];
            if (!handler) {
                this.logError('dispatch', `No handler registered for event: ${event}`);
                return;
            }
            handler.handle(data);
            this._notify();
        } catch (error) {
            this.logError('dispatch', error.message);
        }
    };

    /**
     * Push all files — async, passes onProgress callback so handler can trigger repaints mid-loop.
     */
    dispatchPushAll = async () => {
        try {
            await this._handlers.onPushAll.handle({
                jid: this._jid,
                onProgress: () => this._notify(),
            });
        } catch (error) {
            this.logError('dispatchPushAll', error.message);
        }
    };

    // ─── private ──────────────────────────────────────────

    _notify = () => {
        componentNotifier.notify(_event, _name);
    };
}

const uploadAttachmentHandler = new UploadAttachmentHandler();
export default uploadAttachmentHandler;
